// The customer's own billing (spec 2026-10-05-15): read-only hooks over
// /api/v1/organizations/{orgCode}/billing/*. Needs `billing.read`.
import { $api } from '../api/queryClient'
import type { components } from '../api/schema'

export type BillingSummary = components['schemas']['CustomerBillingSummary']
export type BillingConsumptions = components['schemas']['CustomerBillingConsumptions']
export type BillingInvoice = components['schemas']['CustomerBillingInvoice']
export type BillingInvoiceDetail = components['schemas']['CustomerBillingInvoiceDetail']

/** useBillingSummary loads the summary; `enabled` is false for a caller without `billing.read`. */
export function useBillingSummary(orgCode: string, enabled = true) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/billing/summary',
        { params: { path: { orgCode } } },
        { enabled: !!orgCode && enabled }
    )
}

export function useBillingConsumptions(orgCode: string, enabled = true) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/billing/consumptions',
        { params: { path: { orgCode } } },
        { enabled: !!orgCode && enabled }
    )
}

export function useBillingInvoices(orgCode: string, enabled = true) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/billing/invoices',
        { params: { path: { orgCode } } },
        { enabled: !!orgCode && enabled }
    )
}

export function useBillingInvoice(orgCode: string, uid: string, enabled = true) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/billing/invoices/{uid}',
        { params: { path: { orgCode, uid } } },
        { enabled: !!orgCode && !!uid && enabled }
    )
}

/** billingLabel picks the label of a code in the UI language. */
export function billingLabel(label: Record<string, string> | undefined, code: string, language: string): string {
    if (!label) return code
    return label[language.slice(0, 2)] ?? label.en ?? code
}

/** billingQuantity prints a quantity in its billing unit: bytes are shown in GB, the fixed fee has none. */
export function billingQuantity(quantity: number, divisor: number, unit: string, locale: string): string {
    if (unit === 'month') return '—'
    const value = divisor > 1 ? quantity / divisor : quantity
    const text = new Intl.NumberFormat(locale, { maximumFractionDigits: divisor > 1 ? 2 : 0 }).format(value)
    return divisor > 1 ? `${text} ${unit}` : text
}

/** billingUnitPrice prints a price per billing unit (micro-euros), with "/yr" for a yearly price. */
export function billingUnitPrice(micros: number, locale: string, period?: string): string {
    const text = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 }).format(micros / 1_000_000)
    if (period !== 'year') return text
    return `${text}${locale.startsWith('fr') ? '/an' : '/yr'}`
}
